import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputBase from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import SettingModal from "@/ui/modals/settings/SettingModal";
import AccountSettingModal from "@/ui/modals/settings/AccountSettingModal";
import {
  useArchiveSetting,
  useCreateSetting,
  useSettingsCatalog,
  useUpdateSetting,
} from "@/hooks/useSettingsCatalog";
import {
  useAccountSettings,
  useArchiveAccountSetting,
  useCreateAccountSetting,
  useUpdateAccountSetting,
} from "@/hooks/useAccountSettings";
import {
  AccountSetting,
  Setting,
  SettingApplication,
  SettingType,
} from "@/domain/settings.interface";
import { SettingPayload } from "@/services/settings-catalog.service";
import { AccountSettingPayload } from "@/services/account-settings.service";
import { useAppTheme } from "@/utils/theme";
import { usePageStore } from "@/hooks/usePageStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import useDebounce from "@/hooks/useDebounce";
import { useUserStore } from "@/hooks/useUserStore";
import { ErrorResponse } from "@/domain/error.interface";

type CatalogFilters = {
  search: string;
  application: SettingApplication | "";
  type: SettingType | "";
  active: "" | "true" | "false";
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

type OverrideFilters = {
  settingId: string;
  active: "" | "true" | "false";
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

const DEFAULT_CATALOG_FILTERS: CatalogFilters = {
  search: "",
  application: "",
  type: "",
  active: "",
  pageNumber: 0,
  pageSize: 10,
  sortField: "name",
  sortOrder: 1,
};

const DEFAULT_OVERRIDE_FILTERS: OverrideFilters = {
  settingId: "",
  active: "",
  pageNumber: 0,
  pageSize: 10,
  sortField: "createdAt",
  sortOrder: -1,
};

const APPLICATIONS: SettingApplication[] = ["mobile", "backoffice", "platform"];
const TYPES: SettingType[] = [
  "string",
  "number",
  "boolean",
  "object",
  "array",
  "time",
];

const parseActiveFilter = (value: "" | "true" | "false"): boolean | "" =>
  value === "" ? "" : value === "true";

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    const s = JSON.stringify(value);
    return s.length > 80 ? `${s.slice(0, 77)}...` : s;
  } catch {
    return String(value);
  }
};

const SettingsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const [activeTab, setActiveTab] = useState<number>(0);

  // ---- Catalog state ----
  const [catalogFilters, setCatalogFilters] = useState<CatalogFilters>(
    DEFAULT_CATALOG_FILTERS
  );
  const debouncedCatalogSearch = useDebounce({
    value: catalogFilters.search,
    delay: 500,
  });

  const [settingModal, setSettingModal] = useState<{
    open: boolean;
    setting?: Setting;
  }>({ open: false });
  const [deleteSettingAlert, setDeleteSettingAlert] = useState<{
    open: boolean;
    setting?: Setting;
  }>({ open: false });

  const createSetting = useCreateSetting();
  const updateSetting = useUpdateSetting();
  const archiveSetting = useArchiveSetting();

  const { settings, isLoadingSettings, isFetchingSettings } =
    useSettingsCatalog({
      search: debouncedCatalogSearch,
      application: catalogFilters.application,
      type: catalogFilters.type,
      active: parseActiveFilter(catalogFilters.active),
      pageNumber: catalogFilters.pageNumber,
      pageSize: catalogFilters.pageSize,
      sortField: catalogFilters.sortField,
      sortOrder: catalogFilters.sortOrder,
    });

  // A separate, unfiltered catalog fetch used by the overrides tab to map
  // settingId -> name and to populate the settingId filter.
  const { settings: allSettings } = useSettingsCatalog({
    pageNumber: 0,
    pageSize: 500,
    sortField: "name",
    sortOrder: 1,
  });

  const settingNameById = useMemo(() => {
    const map = new Map<string, string>();
    (allSettings?.list || []).forEach((s) => {
      if (s._id) map.set(s._id, s.name || s._id);
    });
    return map;
  }, [allSettings]);

  // ---- Overrides state ----
  const [overrideFilters, setOverrideFilters] = useState<OverrideFilters>(
    DEFAULT_OVERRIDE_FILTERS
  );

  const [accountSettingModal, setAccountSettingModal] = useState<{
    open: boolean;
    accountSetting?: AccountSetting;
  }>({ open: false });
  const [deleteAccountSettingAlert, setDeleteAccountSettingAlert] = useState<{
    open: boolean;
    accountSetting?: AccountSetting;
  }>({ open: false });

  const createAccountSetting = useCreateAccountSetting();
  const updateAccountSetting = useUpdateAccountSetting();
  const archiveAccountSetting = useArchiveAccountSetting();

  const {
    accountSettings,
    isLoadingAccountSettings,
    isFetchingAccountSettings,
  } = useAccountSettings({
    settingId: overrideFilters.settingId || undefined,
    active: parseActiveFilter(overrideFilters.active),
    pageNumber: overrideFilters.pageNumber,
    pageSize: overrideFilters.pageSize,
    sortField: overrideFilters.sortField,
    sortOrder: overrideFilters.sortOrder,
  });

  useEffect(() => {
    setPageTitle("Settings");
  }, []);

  useEffect(() => {
    setCatalogFilters((prev) => ({ ...prev, pageNumber: 0 }));
  }, [
    debouncedCatalogSearch,
    catalogFilters.application,
    catalogFilters.type,
    catalogFilters.active,
  ]);

  useEffect(() => {
    setOverrideFilters((prev) => ({ ...prev, pageNumber: 0 }));
  }, [overrideFilters.settingId, overrideFilters.active]);

  // ---------- Catalog handlers ----------
  const handleOpenCreateSetting = () => setSettingModal({ open: true });
  const handleOpenEditSetting = (setting: Setting) =>
    setSettingModal({ open: true, setting });
  const handleCloseSettingModal = () => setSettingModal({ open: false });

  const handleSaveSetting = async (data: SettingPayload) => {
    const editing = settingModal.setting;
    try {
      if (editing) {
        await updateSetting.mutateAsync({
          id: editing._id,
          data: { ...data, updatedBy: currentUserId },
        });
        showSnackBar("The setting has been updated.", "success");
      } else {
        await createSetting.mutateAsync({ ...data, createdBy: currentUserId });
        showSnackBar("The setting has been created.", "success");
      }
      handleCloseSettingModal();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const handleConfirmDeleteSetting = async () => {
    const target = deleteSettingAlert.setting;
    if (!target) return;
    try {
      await archiveSetting.mutateAsync(target._id);
      showSnackBar("The registry has been deleted.", "success");
      setDeleteSettingAlert({ open: false });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  // ---------- Overrides handlers ----------
  const handleOpenCreateAccountSetting = () =>
    setAccountSettingModal({ open: true });
  const handleOpenEditAccountSetting = (accountSetting: AccountSetting) =>
    setAccountSettingModal({ open: true, accountSetting });
  const handleCloseAccountSettingModal = () =>
    setAccountSettingModal({ open: false });

  const handleSaveAccountSetting = async (data: AccountSettingPayload) => {
    const editing = accountSettingModal.accountSetting;
    try {
      if (editing) {
        await updateAccountSetting.mutateAsync({
          id: editing._id,
          data: { ...data, updatedBy: currentUserId },
        });
        showSnackBar("The account setting has been updated.", "success");
      } else {
        await createAccountSetting.mutateAsync({
          ...data,
          createdBy: currentUserId,
        });
        showSnackBar("The account setting has been created.", "success");
      }
      handleCloseAccountSettingModal();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const handleConfirmDeleteAccountSetting = async () => {
    const target = deleteAccountSettingAlert.accountSetting;
    if (!target) return;
    try {
      await archiveAccountSetting.mutateAsync(target._id);
      showSnackBar("The registry has been deleted.", "success");
      setDeleteAccountSettingAlert({ open: false });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  // ---------- Catalog table ----------
  const catalogHeadCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "module",
      isSortable: true,
      disablePadding: false,
      label: "Module",
      align: "left",
      width: "140px",
    },
    {
      id: "section",
      isSortable: true,
      disablePadding: false,
      label: "Section",
      align: "left",
      width: "140px",
    },
    {
      id: "application",
      isSortable: true,
      disablePadding: false,
      label: "Application",
      align: "left",
      width: "120px",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "110px",
    },
    {
      id: "default",
      isSortable: false,
      disablePadding: false,
      label: "Default",
      align: "left",
    },
    {
      id: "active",
      isSortable: true,
      disablePadding: false,
      label: "Active",
      align: "left",
      width: "110px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const catalogRows = useMemo(() => {
    if (!settings || settings.list.length === 0) return [];
    return settings.list.map((setting, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEditSetting(setting),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteSettingAlert({ open: true, setting }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: { color: theme.palette.error.main },
        },
      ];

      return createDataRow(index, {
        name: <div>{setting.name}</div>,
        module: (
          <Typography variant="bodyMedium" component="div">
            {setting.module || "-"}
          </Typography>
        ),
        section: (
          <Typography variant="bodyMedium" component="div">
            {setting.section || "-"}
          </Typography>
        ),
        application: (
          <Chip
            size="small"
            label={<div className="text-xs">{setting.application}</div>}
          />
        ),
        type: (
          <Chip
            size="small"
            variant="outlined"
            label={<div className="text-xs">{setting.type}</div>}
          />
        ),
        default: (
          <Typography
            variant="labelMedium"
            component="div"
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "280px",
            }}
            title={(() => {
              try {
                return JSON.stringify(setting.default);
              } catch {
                return String(setting.default);
              }
            })()}
          >
            {stringifyValue(setting.default)}
          </Typography>
        ),
        active: (
          <Chip
            size="small"
            color={setting.active ? "success" : "default"}
            label={
              <div className="text-xs">
                {setting.active ? "Active" : "Inactive"}
              </div>
            }
          />
        ),
        action: <ActionMenu id={setting._id} options={actionMenu} />,
      });
    });
  }, [settings, theme]);

  // ---------- Overrides table ----------
  const overrideHeadCells: HeadCell[] = [
    {
      id: "setting",
      isSortable: false,
      disablePadding: true,
      label: "Setting",
      align: "left",
      width: "260px",
    },
    {
      id: "value",
      isSortable: false,
      disablePadding: false,
      label: "Value",
      align: "left",
    },
    {
      id: "active",
      isSortable: true,
      disablePadding: false,
      label: "Active",
      align: "left",
      width: "110px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const overrideRows = useMemo(() => {
    if (!accountSettings || accountSettings.list.length === 0) return [];
    return accountSettings.list.map((accountSetting, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEditAccountSetting(accountSetting),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () =>
            setDeleteAccountSettingAlert({ open: true, accountSetting }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: { color: theme.palette.error.main },
        },
      ];

      const settingId =
        typeof accountSetting.settingId === "string"
          ? accountSetting.settingId
          : accountSetting.settingId?._id || "";
      const populatedName =
        typeof accountSetting.settingId === "object"
          ? accountSetting.settingId?.name
          : undefined;
      const resolvedName =
        populatedName || settingNameById.get(settingId) || settingId || "-";

      return createDataRow(index, {
        setting: <div>{resolvedName}</div>,
        value: (
          <Typography
            variant="labelMedium"
            component="div"
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "420px",
            }}
            title={(() => {
              try {
                return JSON.stringify(accountSetting.value);
              } catch {
                return String(accountSetting.value);
              }
            })()}
          >
            {stringifyValue(accountSetting.value)}
          </Typography>
        ),
        active: (
          <Chip
            size="small"
            color={accountSetting.active ? "success" : "default"}
            label={
              <div className="text-xs">
                {accountSetting.active ? "Active" : "Inactive"}
              </div>
            }
          />
        ),
        action: <ActionMenu id={accountSetting._id} options={actionMenu} />,
      });
    });
  }, [accountSettings, theme, settingNameById]);

  const isCatalogModalPending =
    createSetting.isPending || updateSetting.isPending;
  const isOverrideModalPending =
    createAccountSetting.isPending || updateAccountSetting.isPending;

  const catalogOptionsForFilter = allSettings?.list || [];

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Settings
                  </Typography>

                  <Button
                    size="medium"
                    onClick={
                      activeTab === 0
                        ? handleOpenCreateSetting
                        : handleOpenCreateAccountSetting
                    }
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <Tabs
                  value={activeTab}
                  onChange={(_e, v) => setActiveTab(v)}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="Global Settings" />
                  <Tab label="Account Overrides" />
                </Tabs>

                {activeTab === 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        width: "100%",
                        flexDirection: {
                          xs: "column",
                          sm: "column",
                          md: "row",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: theme.palette.surface.light2,
                          px: 2,
                          width: { xs: "100%", md: "40%" },
                        }}
                        className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
                      >
                        <InputBase
                          className="flex-1"
                          placeholder="Search setting"
                          inputProps={{ "aria-label": "search" }}
                          onChange={(e) =>
                            setCatalogFilters((f) => ({
                              ...f,
                              search: e.target.value,
                            }))
                          }
                          value={catalogFilters.search}
                        />
                        <SearchIcon className="text-icon-primary" />
                      </Box>

                      <FormControl
                        sx={{ width: { xs: "100%", md: "200px" } }}
                        size="small"
                      >
                        <InputLabel id="settings-application-filter-label">
                          Application
                        </InputLabel>
                        <Select
                          labelId="settings-application-filter-label"
                          label="Application"
                          value={catalogFilters.application}
                          onChange={(e) =>
                            setCatalogFilters((f) => ({
                              ...f,
                              application: e.target
                                .value as CatalogFilters["application"],
                            }))
                          }
                          sx={{ height: "56px", borderRadius: "8px" }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {APPLICATIONS.map((a) => (
                            <MenuItem key={a} value={a}>
                              {a}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl
                        sx={{ width: { xs: "100%", md: "180px" } }}
                        size="small"
                      >
                        <InputLabel id="settings-type-filter-label">
                          Type
                        </InputLabel>
                        <Select
                          labelId="settings-type-filter-label"
                          label="Type"
                          value={catalogFilters.type}
                          onChange={(e) =>
                            setCatalogFilters((f) => ({
                              ...f,
                              type: e.target.value as CatalogFilters["type"],
                            }))
                          }
                          sx={{ height: "56px", borderRadius: "8px" }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                              {t}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl
                        sx={{ width: { xs: "100%", md: "160px" } }}
                        size="small"
                      >
                        <InputLabel id="settings-active-filter-label">
                          Active
                        </InputLabel>
                        <Select
                          labelId="settings-active-filter-label"
                          label="Active"
                          value={catalogFilters.active}
                          onChange={(e) =>
                            setCatalogFilters((f) => ({
                              ...f,
                              active: e.target
                                .value as CatalogFilters["active"],
                            }))
                          }
                          sx={{ height: "56px", borderRadius: "8px" }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="true">Active</MenuItem>
                          <MenuItem value="false">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <DataTable
                      data={catalogRows}
                      headCells={catalogHeadCells}
                      order={catalogFilters.sortOrder === 1 ? "asc" : "desc"}
                      orderBy={catalogFilters.sortField}
                      rowsPerPage={catalogFilters.pageSize}
                      page={catalogFilters.pageNumber}
                      total={settings?.totalCount}
                      isLoading={isLoadingSettings || isFetchingSettings}
                      onChangePage={(_event, newPage) => {
                        setCatalogFilters((f) => ({
                          ...f,
                          pageNumber: newPage,
                        }));
                      }}
                      onChangeSize={(event) => {
                        setCatalogFilters((f) => ({
                          ...f,
                          pageNumber: 0,
                          pageSize: Number(event.target.value),
                        }));
                      }}
                      onChangeSort={(order, orderBy) => {
                        setCatalogFilters((f) => ({
                          ...f,
                          pageNumber: 0,
                          sortField: orderBy as string,
                          sortOrder: order === "asc" ? 1 : -1,
                        }));
                      }}
                      messageEmptyData="No settings to show."
                    />
                  </>
                )}

                {activeTab === 1 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        width: "100%",
                        flexDirection: {
                          xs: "column",
                          sm: "column",
                          md: "row",
                        },
                      }}
                    >
                      <FormControl
                        sx={{ width: { xs: "100%", md: "60%" } }}
                        size="small"
                      >
                        <InputLabel id="account-settings-settingId-filter-label">
                          Setting
                        </InputLabel>
                        <Select
                          labelId="account-settings-settingId-filter-label"
                          label="Setting"
                          value={overrideFilters.settingId}
                          onChange={(e) =>
                            setOverrideFilters((f) => ({
                              ...f,
                              settingId: e.target.value as string,
                            }))
                          }
                          sx={{ height: "56px", borderRadius: "8px" }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {catalogOptionsForFilter.map((s) => (
                            <MenuItem key={s._id} value={s._id}>
                              {s.name}
                              {s.module || s.section
                                ? ` (${s.module || "-"}/${s.section || "-"})`
                                : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl
                        sx={{ width: { xs: "100%", md: "200px" } }}
                        size="small"
                      >
                        <InputLabel id="account-settings-active-filter-label">
                          Active
                        </InputLabel>
                        <Select
                          labelId="account-settings-active-filter-label"
                          label="Active"
                          value={overrideFilters.active}
                          onChange={(e) =>
                            setOverrideFilters((f) => ({
                              ...f,
                              active: e.target
                                .value as OverrideFilters["active"],
                            }))
                          }
                          sx={{ height: "56px", borderRadius: "8px" }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="true">Active</MenuItem>
                          <MenuItem value="false">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <DataTable
                      data={overrideRows}
                      headCells={overrideHeadCells}
                      order={overrideFilters.sortOrder === 1 ? "asc" : "desc"}
                      orderBy={overrideFilters.sortField}
                      rowsPerPage={overrideFilters.pageSize}
                      page={overrideFilters.pageNumber}
                      total={accountSettings?.totalCount}
                      isLoading={
                        isLoadingAccountSettings || isFetchingAccountSettings
                      }
                      onChangePage={(_event, newPage) => {
                        setOverrideFilters((f) => ({
                          ...f,
                          pageNumber: newPage,
                        }));
                      }}
                      onChangeSize={(event) => {
                        setOverrideFilters((f) => ({
                          ...f,
                          pageNumber: 0,
                          pageSize: Number(event.target.value),
                        }));
                      }}
                      onChangeSort={(order, orderBy) => {
                        setOverrideFilters((f) => ({
                          ...f,
                          pageNumber: 0,
                          sortField: orderBy as string,
                          sortOrder: order === "asc" ? 1 : -1,
                        }));
                      }}
                      messageEmptyData="No account settings to show."
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {settingModal.open && (
        <SettingModal
          open={settingModal.open}
          onClose={handleCloseSettingModal}
          setting={settingModal.setting}
          onSave={handleSaveSetting}
          isPending={isCatalogModalPending}
        />
      )}

      {accountSettingModal.open && (
        <AccountSettingModal
          open={accountSettingModal.open}
          onClose={handleCloseAccountSettingModal}
          accountSetting={accountSettingModal.accountSetting}
          onSave={handleSaveAccountSetting}
          isPending={isOverrideModalPending}
        />
      )}

      {deleteSettingAlert.open && (
        <GenericAlertModal
          title="Delete"
          description="Are you sure you want to delete this registry?"
          theme={theme}
          onCancel={() => setDeleteSettingAlert({ open: false })}
          onClick={handleConfirmDeleteSetting}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveSetting.isPending}
        />
      )}

      {deleteAccountSettingAlert.open && (
        <GenericAlertModal
          title="Delete"
          description="Are you sure you want to delete this registry?"
          theme={theme}
          onCancel={() => setDeleteAccountSettingAlert({ open: false })}
          onClick={handleConfirmDeleteAccountSetting}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveAccountSetting.isPending}
        />
      )}
    </>
  );
};

export default SettingsPage;
